import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { login } from '../services/api';
import { showErrorToast, showLoadingToast, updateLoadingToastSuccess, updateLoadingToastError } from '../utils/toastConfig';
import { FiUser, FiLock, FiCheckCircle, FiFileText, FiZap, FiShield } from 'react-icons/fi';
import './Login.css';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { loginUser } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!username || !password) {
      showErrorToast('Please enter username and password');
      setLoading(false);
      return;
    }

    const toastId = showLoadingToast('Logging in...');

    try {
      const { data } = await login({ username, password });
      loginUser(data.user, data.token);
      const role = data.user?.role || 'admin';
      updateLoadingToastSuccess(toastId, `Welcome, ${role === 'hr' ? 'HR' : 'Admin'}!`);
      setTimeout(() => {
        window.location.href = role === 'hr' ? '/crm' : '/dashboard';
      }, 1000);
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Login failed. Please try again.';
      setError(errorMsg);
      updateLoadingToastError(toastId, errorMsg);
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      {/* Left Panel — Branding */}
      <div className="login-brand">
        <div className="login-brand-content">
          <img src="/veramasa.png" alt="Veramasa Logo" className="login-logo" />
          <h1 className="login-brand-title">Invoice Generator</h1>
          <p className="login-brand-tagline">GST-Compliant Automated Invoicing System</p>

          <div className="login-features">
            <div className="login-feature">
              <div className="login-feature-icon">
                <FiShield size={18} />
              </div>
              <div className="login-feature-text">
                <h3>GST Compliance</h3>
                <p>Generate fully compliant GST invoices with automatic CGST, SGST, and IGST calculations</p>
              </div>
            </div>
            <div className="login-feature">
              <div className="login-feature-icon">
                <FiZap size={18} />
              </div>
              <div className="login-feature-text">
                <h3>Automated System</h3>
                <p>Save time with intelligent automation for invoice numbering, tax computation, and PDF export</p>
              </div>
            </div>
            <div className="login-feature">
              <div className="login-feature-icon">
                <FiFileText size={18} />
              </div>
              <div className="login-feature-text">
                <h3>Professional Templates</h3>
                <p>Beautiful, ready-to-use invoice templates with your company branding</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel — Login Form */}
      <div className="login-form-panel">
        <div className="login-form-container">
          <div className="login-form-header">
            <div className="login-form-icon">
              <FiUser size={24} />
            </div>
            <h2>Welcome Back</h2>
            <p>Sign in to access your invoice dashboard</p>
          </div>

          <form onSubmit={handleSubmit} className="login-form">
            {error && (
              <div className="login-error">
                <span>{error}</span>
              </div>
            )}

            <div className="login-field">
              <label>Username or Email</label>
              <div className="login-input-wrap">
                <FiUser className="login-input-icon" size={16} />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter username or email"
                  required
                />
              </div>
            </div>

            <div className="login-field">
              <label>Password</label>
              <div className="login-input-wrap">
                <FiLock className="login-input-icon" size={16} />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  required
                />
              </div>
            </div>

            <button type="submit" className="login-submit-btn" disabled={loading}>
              {loading ? (
                <>
                  <span className="login-spinner"></span>
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </button>

            <div className="login-hint">
              <FiCheckCircle size={13} />
              <span>Two roles available: <strong>Admin</strong> (full access) &middot; <strong>HR</strong> (CRM only)</span>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
